import { FunctionComponent, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'

import HomesForSaleListingCard from 'design-system/Card/HomesForSaleListingCard'
import LinkText from 'design-system/Links/LinkText'
// import PageStepper from 'modules/PageStepper'
import Body1 from 'design-system/Typography/Body/Body1'
import Heading4 from 'design-system/Typography/Heading/Heading4'

import GridContainer from 'components/GlobalStyles/GridContainer'
import Loader from 'components/Loader'
import LoadingBox from 'components/LoadingBox'
import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'
import { prepopulateIntercomMessage } from 'services/intercom'

import { getMapSearchForSaleType } from 'utils/statusHelpers'

import * as T from 'types'
import * as E from 'types/enums'

import { ISort } from '../types'
import ListingSortSelect from './ListingSortSelect'

// const PAGE_SIZE = 24

const ListingResultsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  padding: 24px 24px 0;
  overflow-y: scroll;
  background: ${props => props.theme.colors.white};
  box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.1);
  width: 100vw;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: ${props => props.theme.metrics.smallPhone}px;
  }

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    width: 680px;
  }
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  background: ${props => props.theme.colors.white};
  opacity: 0.5;
  z-index: ${props => props.theme.zIndices.level2};
  cursor: none;

  width: 100vw;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: ${props => props.theme.metrics.smallPhone}px;
  }

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    width: 680px;
  }
`

const ListingResultsHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const ListingResultsDetailRow = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const InfoContainer = styled.div`
  display: flex;
  width: 100%;
  padding-top: 14px;
  @media (min-width: ${props => props.theme.metrics.phone}px) {
    max-width: 500px;
  }
`

const IHaveQuestion = styled.span`
  font-weight: ${props => props.theme.fonts.weights.heavy};
  color: ${props => props.theme.colors.blue3};
  cursor: pointer;
  &:hover {
    color: ${props => props.theme.colors.blue7};
  }
`

const InfoMessage = styled.div`
  padding: 10px 12px;
  background: ${props => props.theme.colors.grey15};
  text-align: center;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const CountRow = styled(FlexRow)`
  justify-content: flex-start;
`

interface IProps {
  address?: string
  count?: number
  initializing?: boolean
  listings: T.IListing[]
  sort: ISort
  sortActive: boolean
  setHighlightedListingId: (listingId: string) => void
  onChangeSort: (sort: ISort) => void
}
const ListingResultsGrid: FunctionComponent<IProps> = ({
  address,
  count,
  listings,
  initializing,
  sort = { key: 'featured', label: 'Featured' },
  sortActive,
  setHighlightedListingId,
  onChangeSort,
}) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isSingleColumn = width && width < theme.metrics.desktopMedium

  // prevents issue where this value is incorrectly set to 2 on first render and get stuck there
  const desktopColumns = useMemo(() => {
    if (!width) return null

    return isSingleColumn ? 1 : 2
  }, [width])

  const askIntercomQuestion = () => {
    prepopulateIntercomMessage("I need help with finding a home that's for sale")
  }

  const sortedListings = useMemo(() => {
    if (!listings?.length) return listings

    return listings.sort((listingA, listingB) => {
      const statusA = getMapSearchForSaleType(listingA)
      const statusB = getMapSearchForSaleType(listingB)

      // sort order should be active, in contract, sold
      if (
        statusA === E.MapSearchForSaleType.forSale
        && statusB !== E.MapSearchForSaleType.forSale
      ) {
        return -1
      }

      if (
        statusA !== E.MapSearchForSaleType.forSale
        && statusB === E.MapSearchForSaleType.forSale
      ) {
        return 1
      }

      if (
        statusA === E.MapSearchForSaleType.inContract
        && statusB !== E.MapSearchForSaleType.inContract
      ) {
        return -1
      }

      if (
        statusA !== E.MapSearchForSaleType.inContract
        && statusB === E.MapSearchForSaleType.inContract
      ) {
        return 1
      }

      if (statusA === E.MapSearchForSaleType.sold && statusB !== E.MapSearchForSaleType.sold) {
        return -1
      }

      if (statusA !== E.MapSearchForSaleType.sold && statusB === E.MapSearchForSaleType.sold) {
        return 1
      }

      return 0
    })
  }, [listings])

  const loading = initializing || typeof count === 'undefined'

  return (
    <>
      {loading && <LoadingOverlay />}
      <ListingResultsContainer>
        <ListingResultsHeader>
          <Heading4>{address ? `${address} Homes For Sale` : '  Homes For Sale'}</Heading4>
          <Spacer size={8} />
          <ListingResultsDetailRow>
            <CountRow>
              <Body1>{`Showing ${listings.length} of `}</Body1> <br />
              &nbsp;
              {!loading
                ? <Body1>{count}</Body1>
                : (
                  <Loader
                    height={20}
                    spin
                  />
                )}
              &nbsp;
              <Body1>Homes For Sale</Body1>
            </CountRow>
            <Spacer size={16} />
            <FlexRow>
              <Body1>Sort by:</Body1>
              <Spacer size={8} />
              <ListingSortSelect
                sort={sort}
                sortActive={sortActive}
                setSort={onChangeSort}
              />
            </FlexRow>
          </ListingResultsDetailRow>
          <FlexRow>
            <LinkText
              href="/partner-offers"
              text="Partner Offers for Homeowners"
            />
          </FlexRow>
        </ListingResultsHeader>
        <Spacer size={24} />
        <CenteredContainer>
          {desktopColumns && (
            <GridContainer
              tabletCols={1}
              desktopCols={desktopColumns}
              largeDesktopCols={2}
              gridGap={24}
            >
              {sortedListings.map(listing => {
                const handleClick = () => setHighlightedListingId(listing.customLinkUrl)
                return (
                  <HomesForSaleListingCard
                    key={listing._id}
                    listing={listing}
                    onClick={handleClick}
                  />
                )
              })}
            </GridContainer>
          )}
        </CenteredContainer>
        <Spacer size={50} />
        <CenteredContainer>
          {!listings.length && !initializing && (
            <InfoContainer>
              <InfoMessage>
                <strong>No match found.</strong>
                <br />
                Try zooming out or entering a city, neighborhood, or zip code.{' '}
                <IHaveQuestion onClick={askIntercomQuestion}>
                  Chat with us for assistance.
                </IHaveQuestion>
              </InfoMessage>
            </InfoContainer>
          )}
          {initializing && <LoadingBox height={200} />}
          {/* <PageStepper
          currentPage={page ? Number(page) : 1}
          pageSize={pageSize || PAGE_SIZE}
          totalItems={100}
          pathname="/listings/homes-for-sale"
        /> */}
          <Spacer size={80} />
        </CenteredContainer>
      </ListingResultsContainer>
    </>
  )
}

export default ListingResultsGrid
